import type { ColumnDef } from "@tanstack/react-table";
import uniq from "lodash/uniq";

import type { IFindingAttr } from "./types";

import type { IEvidenceItem } from "pages/finding/evidence/types";
import { formatLinkHandler } from "utils/format-helpers";
import { translate } from "utils/translations/translate";

const hasEvidencesDrafts = (
  evidenceImages: Record<string, IEvidenceItem>,
): boolean => {
  const keys = uniq([
    "animation",
    "exploitation",
    ...Object.keys(evidenceImages),
  ]);

  return (
    keys.filter((name): boolean => {
      const evidence = evidenceImages[name];

      return evidence.isDraft;
    }).length > 0
  );
};

const getColumns = (): ColumnDef<IFindingAttr>[] => [
  {
    accessorKey: "groupName",
    cell: (cell): JSX.Element => {
      const { groupName } = cell.row.original;
      const link = `/groups/${groupName}`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    header: "Group name",
    size: 200,
  },
  {
    accessorKey: "title",
    cell: (cell): JSX.Element => {
      const { groupName, id, organizationName } = cell.row.original;
      const orgPath =
        organizationName === null ? "" : `/orgs/${organizationName}`;
      const link = `${orgPath}/groups/${groupName}/vulns/${id}/evidence`;
      const text = cell.getValue<string>();

      return formatLinkHandler(link, text);
    },
    header: "Weakness",
    size: 400,
  },
  {
    accessorKey: "hasEvidenceDraft",
    cell: (cell): string => {
      const { hasEvidenceDraft } = cell.row.original;

      return translate.t(
        `todoList.tabs.evidenceDrafts.table.hasLocationDrafts.${
          hasEvidenceDraft ? "yes" : "no"
        }`,
      );
    },
    header: translate.t(
      "todoList.tabs.evidenceDrafts.table.headers.hasEvidenceDraft",
    ),
  },
  {
    accessorKey: "hasLocationDrafts",
    cell: (cell): string => {
      const { hasLocationDrafts } = cell.row.original;

      return translate.t(
        `todoList.tabs.evidenceDrafts.table.hasLocationDrafts.${
          hasLocationDrafts ? "yes" : "no"
        }`,
      );
    },
    header: translate.t(
      "todoList.tabs.evidenceDrafts.table.headers.hasLocationDrafts",
    ),
  },
  {
    accessorKey: "hasApprovedEvidence",
    cell: (cell): string => {
      const { hasApprovedEvidence } = cell.row.original;

      return translate.t(
        `todoList.tabs.evidenceDrafts.table.hasLocationDrafts.${
          hasApprovedEvidence ? "yes" : "no"
        }`,
      );
    },
    header: translate.t(
      "todoList.tabs.evidenceDrafts.table.headers.hasApprovedEvidence",
    ),
    size: 170,
  },
  {
    accessorKey: "hasLocationApproved",
    cell: (cell): string => {
      const { hasLocationApproved } = cell.row.original;

      return translate.t(
        `todoList.tabs.evidenceDrafts.table.hasLocationDrafts.${
          hasLocationApproved ? "yes" : "no"
        }`,
      );
    },
    header: translate.t(
      "todoList.tabs.evidenceDrafts.table.headers.hasLocationApproved",
    ),
  },
];

export { getColumns, hasEvidencesDrafts };
